import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import config from '../config';

const UserAuthContext = createContext();

export const useUserAuth = () => {
  const context = useContext(UserAuthContext);
  if (!context) {
    throw new Error('useUserAuth debe ser usado dentro de UserAuthProvider');
  }
  return context;
};

export const UserAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  const API_URL = config.API_BASE_URL;

  // Verify stored token on mount
  const verifyStoredToken = useCallback(async (storedToken) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${storedToken}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        setToken(storedToken);
        setUser(data.user);
        setIsAuthenticated(true);
      } else {
        localStorage.removeItem('user_token');
        setToken(null);
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Error verificando token de usuario:', error);
      localStorage.removeItem('user_token');
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => {
    const storedToken = localStorage.getItem('user_token');
    if (storedToken) {
      verifyStoredToken(storedToken);
    } else {
      setLoading(false);
    }
  }, [verifyStoredToken]);

  // Helper to set auth state after successful login
  const setAuthState = (authToken, userData) => {
    setToken(authToken);
    setUser(userData);
    setIsAuthenticated(true);
    localStorage.setItem('user_token', authToken);
  };

  // Request magic link
  const requestMagicLink = async (email) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/magic-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error solicitando magic link:', error);
      return { success: false, error: 'Error de conexión' };
    }
  };

  // Verify magic link token
  const verifyMagicLink = async (magicToken) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/verify-magic-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: magicToken })
      });

      const data = await response.json();

      if (data.success) {
        setAuthState(data.token, data.user);
      }

      return data;
    } catch (error) {
      console.error('Error verificando magic link:', error);
      return { success: false, error: 'Error de conexión' };
    }
  };

  // Verify OTP code
  const verifyOTP = async (email, code) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code })
      });

      const data = await response.json();

      if (data.success) {
        setAuthState(data.token, data.user);
      }

      return data;
    } catch (error) {
      console.error('Error verificando OTP:', error);
      return { success: false, error: 'Error de conexión' };
    }
  };

  // Login with Google
  const loginWithGoogle = async (idToken) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken })
      });

      const data = await response.json();

      if (data.success) {
        setAuthState(data.token, data.user);
      }

      return data;
    } catch (error) {
      console.error('Error en login con Google:', error);
      return { success: false, error: 'Error de conexión' };
    }
  };

  // Update profile
  const updateProfile = async (profileData) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(profileData)
      });

      const data = await response.json();

      if (data.success) {
        setUser(data.user);
      }

      return data;
    } catch (error) {
      console.error('Error actualizando perfil:', error);
      return { success: false, error: 'Error de conexión' };
    }
  };

  // Logout
  const logout = () => {
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('user_token');
  };

  const value = {
    user,
    token,
    isAuthenticated,
    loading,
    requestMagicLink,
    verifyMagicLink,
    verifyOTP,
    loginWithGoogle,
    updateProfile,
    logout
  };

  return (
    <UserAuthContext.Provider value={value}>
      {children}
    </UserAuthContext.Provider>
  );
};
